import type { emailDraftReviewToolDef } from '@newfront-insurance/ai-api';
import type { InferToolInput } from 'ai';
import { ExternalLink } from 'lucide-react';
import { useId, useState } from 'react';

import type { EmailDraftPreviewContext } from '../../hooks/outlook-draft/state';

type Recipient = InferToolInput<typeof emailDraftReviewToolDef>['toRecipients'][number];

export function EmailRecipients({ recipients }: { recipients?: Recipient[] }) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const validRecipients =
    recipients?.filter((recipient) => recipient.emailAddress?.address || recipient.emailAddress?.name) ?? [];
  if (!validRecipients.length) return <>None</>;
  const visible = expanded ? validRecipients : validRecipients.slice(0, 4);
  return (
    <div id={id} className="flex flex-wrap items-center gap-1">
      {visible.map(({ emailAddress }, index) => (
        <span
          key={`${emailAddress?.address}-${index}`}
          title={emailAddress?.address ?? undefined}
          className="text-steel-400 max-w-full rounded-full bg-[#FAFBFC] px-1.5 py-[3px] text-[12px] leading-4 font-semibold break-all"
        >
          {emailAddress?.name || emailAddress?.address}
        </span>
      ))}
      {validRecipients.length > 4 && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={id}
          className="text-steel-400 focus-visible:ring-brand-400 rounded-full bg-[#FAFBFC] px-1.5 py-[3px] text-[12px] leading-4 font-semibold focus-visible:ring-2 focus-visible:outline-none"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : `+${validRecipients.length - 4} more`}
        </button>
      )}
    </div>
  );
}

export function EmailDraftReplyContext({
  original,
}: {
  original: NonNullable<EmailDraftPreviewContext['originalMessage']>;
}) {
  const sender = original.from?.emailAddress;
  const received = original.receivedDateTime ? new Date(original.receivedDateTime) : undefined;
  const hasDate = received && !Number.isNaN(received.getTime());
  return (
    <div className="p-[10px]" data-testid="email-draft-reply-context">
      <div className="flex min-w-0 flex-col gap-[10px] rounded-[13px] border border-[#DEE2E7] bg-[#FAFBFC] px-4 py-[10px] text-[12px] leading-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-steel-400 font-semibold">Replying to</span>
          {original.webLink?.startsWith('https://') && (
            <a
              href={original.webLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View source email in Outlook"
              className="focus-visible:ring-brand-400 text-steel-400 flex shrink-0 items-center gap-2 rounded font-semibold hover:underline focus-visible:ring-2 focus-visible:outline-none"
            >
              View source
              <ExternalLink aria-hidden="true" size={14} className="text-[#BEC7D0]" />
            </a>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-x-1">
            <span className="font-semibold" title={sender?.address ?? undefined}>
              {sender?.name || sender?.address || 'Unknown sender'}
            </span>
            {hasDate && (
              <time dateTime={original.receivedDateTime}>
                ·{' '}
                {received.toLocaleString(undefined, {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </time>
            )}
          </div>
          <div className="truncate" title={original.subject}>
            {original.subject || '(no subject)'}
          </div>
          {original.bodyPreview && (
            <div className="truncate" title={original.bodyPreview}>
              {original.bodyPreview}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
